// // screens/marketplace/ProductDetail.jsx
// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import { useNavigate, useParams, Link } from "react-router-dom";
// import { isWholesale, minOrderQty, clampToMoq, moqLabel } from "../../utils/variants";
// import {
//   Heart,
//   Minus,
//   Plus,
//   ShieldCheck,
//   ShoppingBag,
//   Star,
//   Truck,
//   Undo2,
//   ChevronLeft,
//   Package,
//   Clock,
//   AlertCircle,
//   MessageSquare,
//   ChevronRight,
//   BadgeCheck,
//   Ban, Boxes
// } from "lucide-react";
// import { useAuth } from "../../providers/AuthProvider";
// import { useTheme } from "../../hooks/useTheme";
// import { useToast } from "../../providers/ToastProvider";
// import { useCart } from "../../contexts/CartContext";
// import {
//   buildVariantKey,
//   findVariantByKey,
//   collectAttributeValues,
//   availabilityMap,
// } from "../../utils/variants";

// const money = (v) => `GH₵${Number(v || 0).toFixed(2)}`;

// const readError = (data, fallback = "Something went wrong") => {
//   if (!data) return fallback;
//   if (data.message) return data.message;
//   const errs = data.errors;
//   if (errs) {
//     const flat = Object.values(errs).flat().filter(Boolean);
//     if (flat.length) return flat.join(" ");
//   }
//   return fallback;
// };

// export default function ProductDetail() {
//   const { id } = useParams();
//   const navigate = useNavigate();
//   const { authFetch, isAuthenticated } = useAuth();
//   const { colors } = useTheme();
//   const { showToast } = useToast();
//   const { increment: incrementCart } = useCart();

//   const [product, setProduct] = useState(null);
//   const [campaign, setCampaign] = useState(null);
//   const [reviews, setReviews] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [quantity, setQuantity] = useState(1);
//   const [saving, setSaving] = useState(false);
//   const [wishlisting, setWishlisting] = useState(false);
//   const [wishlisted, setWishlisted] = useState(false);
//   const [review, setReview] = useState({ rating: 5, title: "", comment: "" });
//   const [submittingReview, setSubmittingReview] = useState(false);

//   // Variant selection state: { Color: "Red", Size: "M" }
//   const [selection, setSelection] = useState({});

//   // ── Load ──────────────────────────────────────────────────
//   const load = useCallback(async () => {
//     setLoading(true);
//     setError(null);
//     try {
//       const [pRes, cRes, rRes] = await Promise.all([
//         authFetch(`/marketplace/products/${id}/`),
//         authFetch("/marketplace/pre-orders/?is_active=true"),
//         authFetch(`/marketplace/products/${id}/reviews/`),
//       ]);
//       const [pData, cData, rData] = await Promise.all([
//         pRes.json(),
//         cRes.json(),
//         rRes.json(),
//       ]);

//       if (!pRes.ok) throw new Error(readError(pData, "Could not load product"));
//       setProduct(pData.data);

//       if (cRes.ok) {
//         const active = (cData.data || []).find((c) => {
//           const items = c.products || c.pre_order_products || [];
//           return (
//             c.is_active &&
//             items.some(
//               (item) => String(item.product ?? item.product_id) === String(id)
//             )
//           );
//         });
//         setCampaign(active || null);
//       }

//       if (rRes.ok) setReviews(rData.data || []);
//     } catch (err) {
//       setError(err.message);
//     } finally {
//       setLoading(false);
//     }
//   }, [authFetch, id]);

//   useEffect(() => {
//     load();
//   }, [load]);

//   // ── Variant structure ─────────────────────────────────────
//   const hasVariants = !!product?.has_variants;
//   const variants = product?.variants || [];

//   const { names: attributeNames, valuesByName } = useMemo(
//     () =>
//       hasVariants
//         ? collectAttributeValues(variants, product?.variant_attributes)
//         : { names: [], valuesByName: {} },
//     [hasVariants, variants, product?.variant_attributes]
//   );

//   // Auto-select first available value for each attribute on product load.
//   useEffect(() => {
//     if (!hasVariants || attributeNames.length === 0) {
//       setSelection({});
//       return;
//     }
//     const initial = {};
//     attributeNames.forEach((name) => {
//       const avail = availabilityMap(variants, name, initial, attributeNames);
//       const pick = (valuesByName[name] || []).find((v) => avail[v]);
//       if (pick) initial[name] = pick;
//     });
//     setSelection(initial);
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, [product?.id, hasVariants]);

//   // Compute the currently matched variant (only when all attributes are picked).
//   const selectedVariant = useMemo(() => {
//     if (!hasVariants || attributeNames.length === 0) return null;
//     const complete = attributeNames.every((n) => selection[n]);
//     if (!complete) return null;
//     return findVariantByKey(variants, buildVariantKey(selection));
//   }, [hasVariants, attributeNames, selection, variants]);

//   // Which values are still available in each attribute, given the rest of
//   // the selection? Used to disable dead combinations with a hint.
//   const availabilityByAttr = useMemo(() => {
//     if (!hasVariants) return {};
//     const out = {};
//     attributeNames.forEach((name) => {
//       out[name] = availabilityMap(variants, name, selection, attributeNames);
//     });
//     return out;
//   }, [hasVariants, variants, attributeNames, selection]);

//   // ── Campaign ──────────────────────────────────────────────
//   const campaignItem = useMemo(() => {
//     const items = campaign?.products || campaign?.pre_order_products || [];
//     return (
//       items.find(
//         (p) => String(p.product ?? p.product_id) === String(id)
//       ) || null
//     );
//   }, [campaign, id]);

//   const isPreorder = product?.sale_type === "PRE_ORDER" || !!campaign;

//   // ── Pricing ───────────────────────────────────────────────
//   // Priority:
//   //  1. Variant price for the fully-selected variant
//   //  2. Campaign variant price (if campaign exposes per-variant entries)
//   //  3. Campaign product-level price
//   //  4. Parent's effective / base price
//   const displayPrice = useMemo(() => {
//     if (hasVariants && selectedVariant) {
//       return (
//         selectedVariant.effective_price ||
//         selectedVariant.base_price_customer ||
//         product?.effective_price ||
//         product?.base_price_customer
//       );
//     }
//     if (isPreorder) {
//       return (
//         campaignItem?.effective_price ||
//         campaignItem?.price_customer ||
//         product?.effective_pre_order_price ||
//         product?.pre_order_price_customer
//       );
//     }
//     return product?.effective_price || product?.base_price_customer;
//   }, [
//     hasVariants,
//     selectedVariant,
//     isPreorder,
//     campaignItem,
//     product,
//   ]);

//   // ── Quantity / stock ──────────────────────────────────────
//   const maxQty = useMemo(() => {
//     if (hasVariants) {
//       const stock = selectedVariant?.stock;
//       if (stock == null || stock === undefined) return 999;
//       return Math.max(0, Number(stock));
//     }
//     if (isPreorder) return campaignItem?.stock_limit || 999;
//     return product?.stock_quantity || 0;
//   }, [hasVariants, selectedVariant, isPreorder, campaignItem, product]);

//   const isVariantSelectionComplete =
//     !hasVariants || (selectedVariant && attributeNames.every((n) => selection[n]));

//   const isUnavailable = useMemo(() => {
//     if (hasVariants) {
//       if (!isVariantSelectionComplete) return true;
//       return selectedVariant?.is_in_stock === false || maxQty === 0;
//     }
//     if (isPreorder) {
//       return !campaign || maxQty === 0;
//     }
//     return !product?.is_in_stock;
//   }, [
//     hasVariants,
//     isVariantSelectionComplete,
//     selectedVariant,
//     isPreorder,
//     campaign,
//     maxQty,
//     product,
//   ]);

//   // Clamp quantity whenever maxQty shrinks.
//   useEffect(() => {
//     if (maxQty > 0 && quantity > maxQty) setQuantity(maxQty);
//     if (quantity < 1) setQuantity(1);
//   }, [maxQty, quantity]);

//   // ── Status pill ───────────────────────────────────────────
//   const status = useMemo(() => {
//     if (hasVariants) {
//       if (!isVariantSelectionComplete) {
//         return {
//           label: "Pick options",
//           fg: colors.secondaryText,
//           bg: colors.hoverBg,
//           icon: Package,
//         };
//       }
//       if (selectedVariant?.is_in_stock === false || maxQty === 0) {
//         return {
//           label: "Out of stock",
//           fg: colors.secondaryText,
//           bg: colors.hoverBg,
//           icon: AlertCircle,
//         };
//       }
//       return {
//         label: isPreorder ? "Pre-order open" : "Ready to ship",
//         fg: isPreorder ? colors.warning : colors.success,
//         bg: isPreorder ? colors.warningBg : colors.successBg,
//         icon: isPreorder ? Clock : Truck,
//       };
//     }
//     if (isPreorder) {
//       return campaign
//         ? {
//             label: "Pre-order open",
//             fg: colors.warning,
//             bg: colors.warningBg,
//             icon: Clock,
//           }
//         : {
//             label: "Pre-order closed",
//             fg: colors.secondaryText,
//             bg: colors.hoverBg,
//             icon: Clock,
//           };
//     }
//     return product?.is_in_stock
//       ? {
//           label: "Ready to ship",
//           fg: colors.success,
//           bg: colors.successBg,
//           icon: Truck,
//         }
//       : {
//           label: "Out of stock",
//           fg: colors.secondaryText,
//           bg: colors.hoverBg,
//           icon: AlertCircle,
//         };
//   }, [
//     hasVariants,
//     isVariantSelectionComplete,
//     selectedVariant,
//     isPreorder,
//     campaign,
//     product?.is_in_stock,
//     maxQty,
//     colors,
//   ]);

//   const StatusIcon = status.icon;

//   const isAvailableInGhana = product?.sale_type === "AVAILABLE_IN_GHANA";

//   // ── Shipping fee ──────────────────────────────────────────
//   const shippingFee = Number(product?.shipping_fee || 0);

//   // ── Actions ───────────────────────────────────────────────
//   const requireLogin = () => {
//     if (!isAuthenticated) {
//       navigate(`/login?next=/marketplace/products/${id}`);
//       return true;
//     }
//     return false;
//   };

//   const addToCart = async () => {
//     if (requireLogin()) return;
//     if (isUnavailable) return;
//     if (hasVariants && !selectedVariant) {
//       showToast("Choose all options first", "error");
//       return;
//     }
//     setSaving(true);
//     try {
//       const body = {
//         product: id,
//         quantity,
//         item_type: isPreorder ? "PRE_ORDER" : "DROPSHIPPING",
//         ...(isPreorder && campaign && { pre_order: campaign.id }),
//         ...(hasVariants && selectedVariant && { variant_key: selectedVariant.key }),
//       };
//       const res = await authFetch("/marketplace/cart/add/", {
//         method: "POST",
//         body: JSON.stringify(body),
//       });
//       const data = await res.json();
//       if (!res.ok) throw new Error(readError(data, "Could not add to cart"));
//       showToast("Added to your cart", "success");
//       incrementCart(quantity);
//     } catch (err) {
//       showToast(err.message, "error");
//     } finally {
//       setSaving(false);
//     }
//   };

//   const addToWishlist = async () => {
//     if (requireLogin()) return;
//     setWishlisting(true);
//     try {
//       const res = await authFetch("/marketplace/wishlist/add/", {
//         method: "POST",
//         body: JSON.stringify({ product_id: id }),
//       });
//       const data = await res.json();
//       if (!res.ok) throw new Error(readError(data, "Could not update wishlist"));
//       setWishlisted(true);
//       showToast(data.message || "Saved to wishlist", "success");
//     } catch (err) {
//       showToast(err.message, "error");
//     } finally {
//       setWishlisting(false);
//     }
//   };

//   const submitReview = async (e) => {
//     e.preventDefault();
//     if (requireLogin()) return;
//     if (!review.title.trim() || !review.comment.trim()) {
//       showToast("Please fill in a title and comment", "error");
//       return;
//     }
//     setSubmittingReview(true);
//     try {
//       const res = await authFetch(`/marketplace/products/${id}/reviews/`, {
//         method: "POST",
//         body: JSON.stringify(review),
//       });
//       const data = await res.json();
//       if (!res.ok) throw new Error(readError(data, "Could not submit review"));
//       showToast("Review submitted for approval", "success");
//       setReview({ rating: 5, title: "", comment: "" });
//     } catch (err) {
//       showToast(err.message, "error");
//     } finally {
//       setSubmittingReview(false);
//     }
//   };

//   // ── Render: loading ───────────────────────────────────────
//   if (loading) {
//     return (
//       <div style={styles.pageWrap}>
//         <div style={styles.loadingWrap(colors)}>
//           <div style={styles.spinner(colors)} />
//           <span style={{ color: colors.secondaryText, fontSize: 13 }}>
//             Loading product…
//           </span>
//         </div>
//       </div>
//     );
//   }

//   // ── Render: error ─────────────────────────────────────────
//   if (error || !product) {
//     return (
//       <div style={styles.pageWrap}>
//         <div style={styles.errorWrap(colors)}>
//           <div style={styles.errorIcon(colors)}>
//             <AlertCircle size={26} />
//           </div>
//           <h2 style={styles.errorTitle(colors)}>Product not found</h2>
//           <p style={styles.errorText(colors)}>
//             {error || "This product may have been removed."}
//           </p>
//           <Link to="/marketplace" style={styles.primaryCta(colors)}>
//             Back to marketplace
//           </Link>
//         </div>
//       </div>
//     );
//   }

//   const imageSrc = product.main_image_url || product.main_image;

//   return (
//     <div style={styles.pageWrap}>
//       <button
//         type="button"
//         onClick={() => navigate(-1)}
//         style={styles.backBtn(colors)}
//       >
//         <ChevronLeft size={16} />
//         Back
//       </button>

//       <div style={styles.layout} className="product-detail-layout">
//         {/* ── Image ────────────────────────────────────── */}
//         <div style={styles.imageFrame(colors)}>
//           {imageSrc ? (
//             <img src={imageSrc} alt={product.name} style={styles.image} />
//           ) : (
//             <div style={styles.imagePlaceholder(colors)}>
//               <Package size={36} />
//             </div>
//           )}

//           <span
//             style={{
//               position: "absolute",
//               top: 14,
//               left: 14,
//               display: "inline-flex",
//               alignItems: "center",
//               gap: 5,
//               padding: "5px 11px",
//               borderRadius: 999,
//               background: status.bg,
//               color: status.fg,
//               fontSize: 11,
//               fontWeight: 800,
//               letterSpacing: 0.3,
//               textTransform: "uppercase",
//               backdropFilter: "blur(8px)",
//             }}
//           >
//             <StatusIcon size={11} />
//             {status.label}
//           </span>

//           {isAvailableInGhana && (
//             <span
//               style={{
//                 position: "absolute",
//                 top: 14,
//                 right: 14,
//                 display: "inline-flex",
//                 alignItems: "center",
//                 gap: 5,
//                 padding: "5px 11px",
//                 borderRadius: 999,
//                 background: colors.btnBg,
//                 color: colors.btnText,
//                 fontSize: 10.5,
//                 fontWeight: 800,
//                 letterSpacing: 0.3,
//                 textTransform: "uppercase",
//                 boxShadow: `0 2px 8px ${colors.shadow}`,
//               }}
//             >
//               <BadgeCheck size={11} />
//               In Ghana
//             </span>
//           )}
//         </div>

//         {/* ── Info ─────────────────────────────────────── */}
//         <div>
//           <h1 style={styles.title(colors)}>{product.name}</h1>

//           <Rating
//             value={Number(product.average_rating || 0)}
//             count={product.review_count || 0}
//             colors={colors}
//           />

//           <p style={styles.shortDesc(colors)}>
//             {product.short_description || product.description}
//           </p>

//           <div style={styles.priceRow(colors)}>
//             <span style={styles.price(colors)}>{money(displayPrice)}</span>
//             {isPreorder && campaign && (
//               <span style={styles.preorderPill(colors)}>
//                 <Clock size={12} /> Pre-order price
//               </span>
//             )}
//           </div>

//           {/* Pre-order window card */}
//           {isPreorder && (
//             <div
//               style={{
//                 ...styles.windowCard(colors),
//                 background: campaign ? colors.warningBg : colors.hoverBg,
//                 borderColor: campaign ? `${colors.warning}55` : colors.border,
//               }}
//             >
//               <div
//                 style={{
//                   display: "flex",
//                   alignItems: "center",
//                   gap: 8,
//                   marginBottom: 4,
//                   color: campaign ? colors.warning : colors.secondaryText,
//                   fontWeight: 800,
//                   fontSize: 12,
//                   letterSpacing: 0.3,
//                   textTransform: "uppercase",
//                 }}
//               >
//                 <Clock size={13} />
//                 {campaign ? "Order window open" : "No open window"}
//               </div>
//               <p
//                 style={{
//                   margin: 0,
//                   fontSize: 13.5,
//                   lineHeight: 1.55,
//                   color: colors.text,
//                 }}
//               >
//                 {campaign ? (
//                   <>
//                     Order by{" "}
//                     <strong style={{ color: colors.heading }}>
//                       {new Date(campaign.end_date).toLocaleString()}
//                     </strong>
//                     . All orders in this window ship together.
//                   </>
//                 ) : (
//                   "This product is pre-order only. It's currently between campaigns — check back soon."
//                 )}
//               </p>
//               {campaign && (
//                 <Link
//                   to={`/preorder/campaigns/${campaign.id}`}
//                   style={{
//                     display: "inline-flex",
//                     alignItems: "center",
//                     gap: 4,
//                     marginTop: 10,
//                     fontSize: 12.5,
//                     fontWeight: 700,
//                     color: colors.linkColor,
//                     textDecoration: "none",
//                   }}
//                 >
//                   See all products in this window
//                   <ChevronRight size={13} />
//                 </Link>
//               )}
//             </div>
//           )}

//           {/* ── Variant selector ─────────────────────────── */}
//           {hasVariants && attributeNames.length > 0 && (
//             <div style={styles.variantSection}>
//               {attributeNames.map((name) => (
//                 <VariantRow
//                   key={name}
//                   name={name}
//                   values={valuesByName[name] || []}
//                   selection={selection}
//                   availability={availabilityByAttr[name] || {}}
//                   onChange={(value) =>
//                     setSelection((prev) => ({ ...prev, [name]: value }))
//                   }
//                   colors={colors}
//                 />
//               ))}

//               {!isVariantSelectionComplete && (
//                 <div style={styles.variantHint(colors)}>
//                   <AlertCircle size={13} />
//                   Choose a value for every option to see the final price and
//                   availability.
//                 </div>
//               )}

//               {isVariantSelectionComplete &&
//                 !selectedVariant && (
//                   <div style={styles.variantError(colors)}>
//                     <Ban size={13} />
//                     This combination is not available. Try another.
//                   </div>
//                 )}
//             </div>
//           )}

//           {/* ── Quantity ─────────────────────────────────── */}
//           {!isUnavailable && (
//             <div style={{ marginTop: 20 }}>
//               <div style={styles.qtyLabel(colors)}>
//                 Quantity
//                 {maxQty > 0 && maxQty < 999 && (
//                   <span style={{ color: colors.secondaryText, fontWeight: 500 }}>
//                     {" "}
//                     · {maxQty} available
//                   </span>
//                 )}
//               </div>
//               <div style={styles.qtyGroup(colors)}>
//                 <button
//                   type="button"
//                   aria-label="Decrease quantity"
//                   disabled={quantity <= 1}
//                   onClick={() => setQuantity((q) => Math.max(1, q - 1))}
//                   style={styles.qtyBtn(colors, quantity <= 1)}
//                 >
//                   <Minus size={15} />
//                 </button>
//                 <span
//                   style={{
//                     minWidth: 40,
//                     textAlign: "center",
//                     fontWeight: 800,
//                     fontSize: 15,
//                     color: colors.heading,
//                   }}
//                 >
//                   {quantity}
//                 </span>
//                 <button
//                   type="button"
//                   aria-label="Increase quantity"
//                   disabled={quantity >= maxQty}
//                   onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
//                   style={styles.qtyBtn(colors, quantity >= maxQty)}
//                 >
//                   <Plus size={15} />
//                 </button>
//               </div>
//             </div>
//           )}

//           {/* ── Actions ──────────────────────────────────── */}
//           <div style={styles.actionsRow}>
//             <button
//               type="button"
//               disabled={isUnavailable || saving}
//               onClick={addToCart}
//               style={styles.primaryAction(colors, isUnavailable || saving)}
//             >
//               <ShoppingBag size={17} />
//               {saving
//                 ? "Adding…"
//                 : isUnavailable
//                 ? hasVariants && !isVariantSelectionComplete
//                   ? "Pick options"
//                   : "Unavailable"
//                 : isPreorder
//                 ? "Join pre-order"
//                 : "Add to cart"}
//             </button>
//             <button
//               type="button"
//               aria-label="Save to wishlist"
//               disabled={wishlisting || wishlisted}
//               onClick={addToWishlist}
//               style={styles.wishlistBtn(colors, wishlisted)}
//             >
//               <Heart size={19} fill={wishlisted ? colors.error : "none"} />
//             </button>
//           </div>

//           {/* ── Shipping + benefits ──────────────────────── */}
//           <div style={styles.shippingLine(colors)}>
//             <Truck size={14} color={shippingFee > 0 ? colors.text : colors.success} />
//             <span
//               style={{
//                 color: shippingFee > 0 ? colors.text : colors.success,
//                 fontWeight: 700,
//               }}
//             >
//               {shippingFee > 0
//                 ? `Ships for ${money(shippingFee)}`
//                 : "Free shipping"}
//             </span>
//           </div>

//           <div style={styles.benefitsGrid}>
//             <Benefit
//               icon={ShieldCheck}
//               title="Protected payment"
//               copy="Wallet or Paystack secured"
//               colors={colors}
//             />
//             <Benefit
//               icon={Undo2}
//               title="Order support"
//               copy="Track every order"
//               colors={colors}
//             />
//             <Benefit
//               icon={Package}
//               title="Careful packing"
//               copy="Inspect before shipping"
//               colors={colors}
//             />
//           </div>
//         </div>
//       </div>

//       {/* ── Description ──────────────────────────────── */}
//       <section style={styles.section(colors)}>
//         <SectionHead
//           title="About this product"
//           subtitle="Product information supplied by Azoemart."
//           colors={colors}
//         />
//         <p style={styles.body(colors)}>
//           {product.description || "No further description provided."}
//         </p>
//       </section>

//       {/* ── Reviews ──────────────────────────────────── */}
//       <section style={styles.section(colors)}>
//         <SectionHead
//           title="Customer reviews"
//           subtitle={`${reviews.length} approved review${
//             reviews.length === 1 ? "" : "s"
//           }`}
//           colors={colors}
//         />

//         {reviews.length === 0 ? (
//           <div style={styles.noReviews(colors)}>
//             <MessageSquare size={22} />
//             <p style={{ margin: "8px 0 0" }}>
//               No approved reviews yet. Be the first to share your experience.
//             </p>
//           </div>
//         ) : (
//           <div style={{ display: "grid", gap: 14 }}>
//             {reviews.map((r) => (
//               <article key={r.id} style={styles.reviewCard(colors)}>
//                 <div style={styles.reviewHeader}>
//                   <div>
//                     <div style={styles.reviewTitle(colors)}>{r.title}</div>
//                     <div style={styles.reviewMeta(colors)}>
//                       {r.username} ·{" "}
//                       {new Date(r.created_at).toLocaleDateString()}
//                     </div>
//                   </div>
//                   <Stars value={r.rating} colors={colors} size={13} />
//                 </div>
//                 <p style={styles.reviewBody(colors)}>{r.comment}</p>
//               </article>
//             ))}
//           </div>
//         )}

//         <form onSubmit={submitReview} style={styles.reviewForm(colors)}>
//           <h3 style={styles.reviewFormTitle(colors)}>Write a review</h3>
//           <p style={styles.reviewFormSub(colors)}>
//             Reviews are moderated before appearing publicly.
//           </p>

//           <div style={styles.formGrid} className="product-form-grid">
//             <label style={styles.fieldWrap}>
//               <span style={styles.fieldLabel(colors)}>Rating</span>
//               <select
//                 value={review.rating}
//                 onChange={(e) =>
//                   setReview({ ...review, rating: Number(e.target.value) })
//                 }
//                 style={styles.select(colors)}
//               >
//                 {[5, 4, 3, 2, 1].map((n) => (
//                   <option key={n} value={n}>
//                     {n} star{n === 1 ? "" : "s"}
//                   </option>
//                 ))}
//               </select>
//             </label>

//             <label style={styles.fieldWrap}>
//               <span style={styles.fieldLabel(colors)}>Title</span>
//               <input
//                 required
//                 value={review.title}
//                 onChange={(e) =>
//                   setReview({ ...review, title: e.target.value })
//                 }
//                 placeholder="Short headline"
//                 style={styles.input(colors)}
//               />
//             </label>
//           </div>

//           <label style={{ ...styles.fieldWrap, marginTop: 12 }}>
//             <span style={styles.fieldLabel(colors)}>Comment</span>
//             <textarea
//               required
//               rows={4}
//               value={review.comment}
//               onChange={(e) =>
//                 setReview({ ...review, comment: e.target.value })
//               }
//               placeholder="Tell other customers about your experience"
//               style={styles.textarea(colors)}
//             />
//           </label>

//           <div
//             style={{
//               marginTop: 14,
//               display: "flex",
//               justifyContent: "flex-end",
//             }}
//           >
//             <button
//               type="submit"
//               disabled={submittingReview}
//               style={styles.submitReview(colors, submittingReview)}
//             >
//               {submittingReview ? "Submitting…" : "Submit review"}
//             </button>
//           </div>
//         </form>
//       </section>

//       <style>{`
//         @media (min-width: 900px) {
//           .product-detail-layout {
//             grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr) !important;
//           }
//         }
//         @media (min-width: 640px) {
//           .product-form-grid {
//             grid-template-columns: 160px minmax(0, 1fr) !important;
//           }
//         }
//         @keyframes spin { to { transform: rotate(360deg); } }
//       `}</style>
//     </div>
//   );
// }

// /* ══════════════════════════════════════════════════════════
//    Variant row
//    ══════════════════════════════════════════════════════════ */

// const VariantRow = ({ name, values, selection, availability, onChange, colors }) => {
//   const currentValue = selection[name];
//   return (
//     <div style={styles.variantRow}>
//       <div style={styles.variantLabel(colors)}>
//         {name}
//         {currentValue && (
//           <span style={styles.variantPicked(colors)}>{currentValue}</span>
//         )}
//       </div>
//       <div style={styles.variantPills}>
//         {values.map((v) => {
//           const enabled = availability[v] !== false;
//           const active = currentValue === v;
//           return (
//             <button
//               key={v}
//               type="button"
//               disabled={!enabled}
//               onClick={() => enabled && onChange(v)}
//               title={
//                 enabled
//                   ? undefined
//                   : currentValue
//                   ? `Not available with ${currentValue}`
//                   : "Not available"
//               }
//               style={styles.variantPill(colors, active, !enabled)}
//             >
//               {v}
//             </button>
//           );
//         })}
//       </div>
//     </div>
//   );
// };

// /* ══════════════════════════════════════════════════════════
//    Small building blocks
//    ══════════════════════════════════════════════════════════ */

// const Stars = ({ value = 0, colors, size = 14 }) => {
//   const rounded = Math.round(value * 2) / 2;
//   return (
//     <span style={{ display: "inline-flex", gap: 1 }}>
//       {[1, 2, 3, 4, 5].map((i) => {
//         const fill =
//           i <= Math.floor(rounded)
//             ? "full"
//             : i - 0.5 === rounded
//             ? "half"
//             : "empty";
//         return (
//           <Star
//             key={i}
//             size={size}
//             strokeWidth={1.5}
//             color={colors.warning}
//             fill={fill === "full" ? colors.warning : "none"}
//           />
//         );
//       })}
//     </span>
//   );
// };

// const Rating = ({ value, count, colors }) => (
//   <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
//     <Stars value={value} colors={colors} size={15} />
//     <span style={{ fontSize: 13, color: colors.secondaryText }}>
//       {value > 0 ? value.toFixed(1) : "New"} · {count} review
//       {count === 1 ? "" : "s"}
//     </span>
//   </div>
// );

// const Benefit = ({ icon: Icon, title, copy, colors }) => (
//   <div
//     style={{
//       padding: 12,
//       borderRadius: 12,
//       border: `1px solid ${colors.border}`,
//       background: colors.raisedBg,
//     }}
//   >
//     <Icon size={16} color={colors.btnBg} />
//     <div
//       style={{
//         fontSize: 12.5,
//         fontWeight: 700,
//         color: colors.heading,
//         marginTop: 8,
//         marginBottom: 2,
//       }}
//     >
//       {title}
//     </div>
//     <div
//       style={{
//         fontSize: 11.5,
//         color: colors.secondaryText,
//         lineHeight: 1.4,
//       }}
//     >
//       {copy}
//     </div>
//   </div>
// );

// const SectionHead = ({ title, subtitle, colors }) => (
//   <div style={{ marginBottom: 14 }}>
//     <h2
//       style={{
//         margin: 0,
//         fontSize: 17,
//         fontWeight: 800,
//         color: colors.heading,
//         letterSpacing: "-0.01em",
//       }}
//     >
//       {title}
//     </h2>
//     {subtitle && (
//       <p
//         style={{
//           margin: "4px 0 0",
//           fontSize: 13,
//           color: colors.secondaryText,
//         }}
//       >
//         {subtitle}
//       </p>
//     )}
//   </div>
// );

// /* ══════════════════════════════════════════════════════════
//    Styles
//    ══════════════════════════════════════════════════════════ */

// const styles = {
//   pageWrap: {
//     maxWidth: 1180,
//     margin: "0 auto",
//     padding: "16px 16px 64px",
//   },

//   backBtn: (c) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 4,
//     background: "transparent",
//     border: "none",
//     color: c.secondaryText,
//     cursor: "pointer",
//     padding: "6px 0",
//     marginBottom: 14,
//     fontSize: 13,
//     fontWeight: 600,
//   }),

//   layout: {
//     display: "grid",
//     gridTemplateColumns: "minmax(0, 1fr)",
//     gap: 28,
//     marginBottom: 40,
//   },

//   imageFrame: (c) => ({
//     position: "relative",
//     aspectRatio: "1 / 1",
//     borderRadius: 18,
//     overflow: "hidden",
//     border: `1px solid ${c.border}`,
//     background: c.subtleBg || c.hoverBg,
//   }),

//   image: {
//     width: "100%",
//     height: "100%",
//     objectFit: "cover",
//     display: "block",
//   },

//   imagePlaceholder: (c) => ({
//     width: "100%",
//     height: "100%",
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     color: c.secondaryText,
//   }),

//   title: (c) => ({
//     margin: 0,
//     fontSize: "clamp(22px, 3vw, 30px)",
//     fontWeight: 800,
//     letterSpacing: "-0.02em",
//     lineHeight: 1.15,
//     color: c.heading,
//   }),

//   shortDesc: (c) => ({
//     margin: "16px 0 0",
//     fontSize: 14.5,
//     lineHeight: 1.6,
//     color: c.secondaryText,
//   }),

//   priceRow: (c) => ({
//     display: "flex",
//     alignItems: "center",
//     gap: 10,
//     flexWrap: "wrap",
//     marginTop: 18,
//   }),

//   price: (c) => ({
//     fontSize: 28,
//     fontWeight: 800,
//     letterSpacing: "-0.02em",
//     color: c.heading,
//   }),

//   preorderPill: (c) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 4,
//     padding: "4px 10px",
//     borderRadius: 999,
//     background: c.warningBg,
//     color: c.warning,
//     fontSize: 11,
//     fontWeight: 700,
//   }),

//   windowCard: (c) => ({
//     marginTop: 18,
//     padding: 14,
//     borderRadius: 12,
//     border: `1px solid ${c.border}`,
//   }),

//   // ── Variant selector ─────────────────────────────────
//   variantSection: {
//     marginTop: 22,
//     display: "grid",
//     gap: 14,
//   },
//   variantRow: {
//     display: "grid",
//     gap: 8,
//   },
//   variantLabel: (c) => ({
//     display: "flex",
//     alignItems: "center",
//     gap: 8,
//     fontSize: 12.5,
//     fontWeight: 700,
//     letterSpacing: 0.3,
//     textTransform: "uppercase",
//     color: c.secondaryText,
//   }),
//   variantPicked: (c) => ({
//     fontSize: 11,
//     fontWeight: 700,
//     letterSpacing: 0.2,
//     padding: "2px 8px",
//     borderRadius: 999,
//     background: c.hoverBg,
//     color: c.text,
//     textTransform: "none",
//   }),
//   variantPills: {
//     display: "flex",
//     flexWrap: "wrap",
//     gap: 8,
//   },
//   variantPill: (c, active, disabled) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     minWidth: 44,
//     padding: "8px 14px",
//     borderRadius: 10,
//     border: `1px solid ${
//       disabled ? c.border : active ? c.btnBg : c.border
//     }`,
//     background: disabled
//       ? c.hoverBg
//       : active
//       ? c.btnBg
//       : c.raisedBg,
//     color: disabled
//       ? c.secondaryText
//       : active
//       ? c.btnText
//       : c.text,
//     fontSize: 13.5,
//     fontWeight: 700,
//     fontFamily: "inherit",
//     cursor: disabled ? "not-allowed" : "pointer",
//     textDecoration: disabled ? "line-through" : "none",
//     opacity: disabled ? 0.6 : 1,
//     transition: "background 0.15s ease, border-color 0.15s ease",
//   }),

//   variantHint: (c) => ({
//     display: "flex",
//     alignItems: "center",
//     gap: 6,
//     fontSize: 12,
//     color: c.secondaryText,
//     paddingTop: 2,
//   }),
//   variantError: (c) => ({
//     display: "flex",
//     alignItems: "center",
//     gap: 6,
//     fontSize: 12.5,
//     fontWeight: 600,
//     color: c.error,
//     paddingTop: 2,
//   }),

//   // ── Quantity ─────────────────────────────────────────
//   qtyLabel: (c) => ({
//     fontSize: 13,
//     fontWeight: 700,
//     color: c.text,
//     marginBottom: 8,
//   }),
//   qtyGroup: (c) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     border: `1px solid ${c.border}`,
//     borderRadius: 12,
//     background: c.raisedBg,
//     overflow: "hidden",
//   }),
//   qtyBtn: (c, disabled) => ({
//     width: 40,
//     height: 40,
//     border: "none",
//     background: "transparent",
//     color: disabled ? c.secondaryText : c.text,
//     cursor: disabled ? "not-allowed" : "pointer",
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     opacity: disabled ? 0.4 : 1,
//   }),

//   // ── Actions ──────────────────────────────────────────
//   actionsRow: {
//     display: "grid",
//     gridTemplateColumns: "minmax(0, 1fr) auto",
//     gap: 10,
//     marginTop: 22,
//   },
//   primaryAction: (c, disabled) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     gap: 8,
//     padding: "13px 22px",
//     borderRadius: 12,
//     border: "none",
//     background: disabled ? c.subtleBg || c.hoverBg : c.btnBg,
//     color: disabled ? c.secondaryText : c.btnText,
//     fontSize: 14.5,
//     fontWeight: 700,
//     cursor: disabled ? "not-allowed" : "pointer",
//     transition: "transform 0.15s ease, box-shadow 0.15s ease",
//     boxShadow: disabled ? "none" : `0 6px 20px ${c.btnBg}33`,
//   }),
//   wishlistBtn: (c, active) => ({
//     width: 52,
//     height: 52,
//     borderRadius: 12,
//     border: `1px solid ${active ? c.error : c.border}`,
//     background: active ? c.errorBg : c.raisedBg,
//     color: active ? c.error : c.text,
//     cursor: "pointer",
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     transition: "border-color 0.15s ease, background 0.15s ease",
//   }),

//   // ── Shipping line ────────────────────────────────────
//   shippingLine: (c) => ({
//     display: "flex",
//     alignItems: "center",
//     gap: 8,
//     marginTop: 18,
//     padding: "10px 14px",
//     borderRadius: 10,
//     background: c.subtleBg || c.hoverBg,
//     border: `1px solid ${c.border}`,
//     fontSize: 13,
//   }),

//   benefitsGrid: {
//     display: "grid",
//     gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
//     gap: 10,
//     marginTop: 14,
//   },

//   // ── Sections ─────────────────────────────────────────
//   section: (c) => ({
//     background: c.raisedBg,
//     border: `1px solid ${c.border}`,
//     borderRadius: 16,
//     padding: 20,
//     marginBottom: 20,
//   }),
//   body: (c) => ({
//     margin: 0,
//     fontSize: 14,
//     lineHeight: 1.7,
//     color: c.text,
//   }),

//   // ── Reviews ──────────────────────────────────────────
//   noReviews: (c) => ({
//     padding: 28,
//     textAlign: "center",
//     color: c.secondaryText,
//     border: `1px dashed ${c.border}`,
//     borderRadius: 12,
//     fontSize: 13,
//   }),
//   reviewCard: (c) => ({
//     padding: 14,
//     borderRadius: 12,
//     border: `1px solid ${c.border}`,
//     background: c.cardBg,
//   }),
//   reviewHeader: {
//     display: "flex",
//     justifyContent: "space-between",
//     alignItems: "flex-start",
//     gap: 10,
//     marginBottom: 8,
//   },
//   reviewTitle: (c) => ({
//     fontSize: 14,
//     fontWeight: 700,
//     color: c.heading,
//     marginBottom: 2,
//   }),
//   reviewMeta: (c) => ({
//     fontSize: 11.5,
//     color: c.secondaryText,
//   }),
//   reviewBody: (c) => ({
//     margin: 0,
//     fontSize: 13.5,
//     lineHeight: 1.6,
//     color: c.text,
//   }),
//   reviewForm: (c) => ({
//     marginTop: 22,
//     paddingTop: 20,
//     borderTop: `1px solid ${c.separator || c.border}`,
//   }),
//   reviewFormTitle: (c) => ({
//     margin: 0,
//     fontSize: 15,
//     fontWeight: 800,
//     color: c.heading,
//   }),
//   reviewFormSub: (c) => ({
//     margin: "4px 0 16px",
//     fontSize: 12.5,
//     color: c.secondaryText,
//   }),
//   formGrid: {
//     display: "grid",
//     gridTemplateColumns: "minmax(0, 1fr)",
//     gap: 12,
//   },
//   fieldWrap: {
//     display: "block",
//     minWidth: 0,
//   },
//   fieldLabel: (c) => ({
//     display: "block",
//     fontSize: 12,
//     fontWeight: 600,
//     color: c.text,
//     marginBottom: 6,
//   }),
//   input: (c) => ({
//     width: "100%",
//     boxSizing: "border-box",
//     padding: "10px 12px",
//     borderRadius: 10,
//     border: `1px solid ${c.border}`,
//     background: c.raisedBg,
//     color: c.text,
//     fontSize: 14,
//     outline: "none",
//     fontFamily: "inherit",
//   }),
//   select: (c) => ({
//     width: "100%",
//     boxSizing: "border-box",
//     padding: "10px 12px",
//     borderRadius: 10,
//     border: `1px solid ${c.border}`,
//     background: c.raisedBg,
//     color: c.text,
//     fontSize: 14,
//     outline: "none",
//     fontFamily: "inherit",
//   }),
//   textarea: (c) => ({
//     width: "100%",
//     boxSizing: "border-box",
//     padding: "10px 12px",
//     borderRadius: 10,
//     border: `1px solid ${c.border}`,
//     background: c.raisedBg,
//     color: c.text,
//     fontSize: 14,
//     outline: "none",
//     fontFamily: "inherit",
//     resize: "vertical",
//     minHeight: 96,
//   }),
//   submitReview: (c, disabled) => ({
//     padding: "10px 20px",
//     borderRadius: 10,
//     border: "none",
//     background: disabled ? c.subtleBg || c.hoverBg : c.btnBg,
//     color: disabled ? c.secondaryText : c.btnText,
//     fontSize: 13.5,
//     fontWeight: 700,
//     cursor: disabled ? "not-allowed" : "pointer",
//     transition: "transform 0.15s ease",
//   }),

//   // ── Loading / error ──────────────────────────────────
//   loadingWrap: (c) => ({
//     display: "flex",
//     flexDirection: "column",
//     alignItems: "center",
//     justifyContent: "center",
//     minHeight: 360,
//     gap: 14,
//   }),
//   spinner: (c) => ({
//     width: 34,
//     height: 34,
//     border: `3px solid ${c.border}`,
//     borderTopColor: c.btnBg,
//     borderRadius: "50%",
//     animation: "spin 0.9s linear infinite",
//   }),
//   errorWrap: (c) => ({
//     maxWidth: 380,
//     margin: "60px auto",
//     textAlign: "center",
//     padding: 24,
//   }),
//   errorIcon: (c) => ({
//     width: 64,
//     height: 64,
//     borderRadius: 20,
//     background: c.hoverBg,
//     color: c.secondaryText,
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     marginBottom: 16,
//   }),
//   errorTitle: (c) => ({
//     margin: "0 0 6px",
//     fontSize: 18,
//     fontWeight: 800,
//     color: c.heading,
//   }),
//   errorText: (c) => ({
//     margin: "0 0 22px",
//     fontSize: 13.5,
//     color: c.secondaryText,
//     lineHeight: 1.55,
//   }),
//   primaryCta: (c) => ({
//     display: "inline-block",
//     padding: "11px 20px",
//     borderRadius: 10,
//     background: c.btnBg,
//     color: c.btnText,
//     fontWeight: 700,
//     fontSize: 13.5,
//     textDecoration: "none",
//   }),
// };




























// screens/marketplace/ProductDetail.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Heart,
  Minus,
  Plus,
  ShieldCheck,
  ShoppingBag,
  Star,
  Truck,
  Undo2,
  ChevronLeft,
  Package,
  Clock,
  AlertCircle,
  MessageSquare,
  ChevronRight,
  BadgeCheck,
  Ban,
  Boxes,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useTheme } from "../../hooks/useTheme";
import { useToast } from "../../providers/ToastProvider";
import { useCart } from "../../contexts/CartContext";
import {
  buildVariantKey,
  findVariantByKey,
  collectAttributeValues,
  availabilityMap,
  isWholesale,
  minOrderQty,
} from "../../utils/variants";

const money = (v) => `GH₵${Number(v || 0).toFixed(2)}`;

const readError = (data, fallback = "Something went wrong") => {
  if (!data) return fallback;
  if (data.message) return data.message;
  const errs = data.errors;
  if (errs) {
    const flat = Object.values(errs).flat().filter(Boolean);
    if (flat.length) return flat.join(" ");
  }
  return fallback;
};

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authFetch, isAuthenticated } = useAuth();
  const { colors } = useTheme();
  const { showToast } = useToast();
  const { increment: incrementCart } = useCart();

  const [product, setProduct] = useState(null);
  const [campaign, setCampaign] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [saving, setSaving] = useState(false);
  const [wishlisting, setWishlisting] = useState(false);
  const [wishlisted, setWishlisted] = useState(false);
  const [review, setReview] = useState({ rating: 5, title: "", comment: "" });
  const [submittingReview, setSubmittingReview] = useState(false);

  // Variant selection state: { Color: "Red", Size: "M" }
  const [selection, setSelection] = useState({});

  // ── Load ──────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [pRes, cRes, rRes] = await Promise.all([
        authFetch(`/marketplace/products/${id}/`),
        authFetch("/marketplace/pre-orders/?is_active=true"),
        authFetch(`/marketplace/products/${id}/reviews/`),
      ]);
      const [pData, cData, rData] = await Promise.all([
        pRes.json(),
        cRes.json(),
        rRes.json(),
      ]);

      if (!pRes.ok) throw new Error(readError(pData, "Could not load product"));
      setProduct(pData.data);

      if (cRes.ok) {
        const active = (cData.data || []).find((c) => {
          const items = c.products || c.pre_order_products || [];
          return (
            c.is_active &&
            items.some(
              (item) => String(item.product ?? item.product_id) === String(id)
            )
          );
        });
        setCampaign(active || null);
      }

      if (rRes.ok) setReviews(rData.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch, id]);

  useEffect(() => {
    load();
  }, [load]);

  // ── Variant structure ─────────────────────────────────────
  const hasVariants = !!product?.has_variants;
  const variants = product?.variants || [];

  const { names: attributeNames, valuesByName } = useMemo(
    () =>
      hasVariants
        ? collectAttributeValues(variants, product?.variant_attributes)
        : { names: [], valuesByName: {} },
    [hasVariants, variants, product?.variant_attributes]
  );

  // Auto-select first available value for each attribute on product load.
  useEffect(() => {
    if (!hasVariants || attributeNames.length === 0) {
      setSelection({});
      return;
    }
    const initial = {};
    attributeNames.forEach((name) => {
      const avail = availabilityMap(variants, name, initial, attributeNames);
      const pick = (valuesByName[name] || []).find((v) => avail[v]);
      if (pick) initial[name] = pick;
    });
    setSelection(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id, hasVariants]);

  // Compute the currently matched variant (only when all attributes are picked).
  const selectedVariant = useMemo(() => {
    if (!hasVariants || attributeNames.length === 0) return null;
    const complete = attributeNames.every((n) => selection[n]);
    if (!complete) return null;
    return findVariantByKey(variants, buildVariantKey(selection));
  }, [hasVariants, attributeNames, selection, variants]);

  // Which values are still available in each attribute, given the rest of
  // the selection? Used to disable dead combinations with a hint.
  const availabilityByAttr = useMemo(() => {
    if (!hasVariants) return {};
    const out = {};
    attributeNames.forEach((name) => {
      out[name] = availabilityMap(variants, name, selection, attributeNames);
    });
    return out;
  }, [hasVariants, variants, attributeNames, selection]);

  // ── Campaign ──────────────────────────────────────────────
  const campaignItem = useMemo(() => {
    const items = campaign?.products || campaign?.pre_order_products || [];
    return (
      items.find(
        (p) => String(p.product ?? p.product_id) === String(id)
      ) || null
    );
  }, [campaign, id]);

  const isPreorder = product?.sale_type === "PRE_ORDER" || !!campaign;

  // ── Pricing ───────────────────────────────────────────────
  const displayPrice = useMemo(() => {
    if (hasVariants && selectedVariant) {
      return (
        selectedVariant.effective_price ||
        selectedVariant.base_price_customer ||
        product?.effective_price ||
        product?.base_price_customer
      );
    }
    if (isPreorder) {
      return (
        campaignItem?.effective_price ||
        campaignItem?.price_customer ||
        product?.effective_pre_order_price ||
        product?.pre_order_price_customer
      );
    }
    return product?.effective_price || product?.base_price_customer;
  }, [hasVariants, selectedVariant, isPreorder, campaignItem, product]);

  // ── Quantity / stock ──────────────────────────────────────
  const maxQty = useMemo(() => {
    if (hasVariants) {
      const stock = selectedVariant?.stock;
      if (stock == null || stock === undefined) return 999;
      return Math.max(0, Number(stock));
    }
    if (isPreorder) return campaignItem?.stock_limit || 999;
    return product?.stock_quantity || 0;
  }, [hasVariants, selectedVariant, isPreorder, campaignItem, product]);

  // Wholesale: minimum order quantity (1 for regular products).
  const minQty = useMemo(() => minOrderQty(product), [product]);

  const isVariantSelectionComplete =
    !hasVariants || (selectedVariant && attributeNames.every((n) => selection[n]));

  const isUnavailable = useMemo(() => {
    if (hasVariants) {
      if (!isVariantSelectionComplete) return true;
      return selectedVariant?.is_in_stock === false || maxQty === 0;
    }
    if (isPreorder) {
      return !campaign || maxQty === 0;
    }
    return !product?.is_in_stock;
  }, [
    hasVariants,
    isVariantSelectionComplete,
    selectedVariant,
    isPreorder,
    campaign,
    maxQty,
    product,
  ]);

  // ── Quantity seeding + clamping ───────────────────────────
  // When the product loads, seed quantity at MOQ for wholesale products.
  useEffect(() => {
    if (!product) return;
    setQuantity(minOrderQty(product));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  // Clamp whenever maxQty shrinks or the floor changes.
  useEffect(() => {
    if (maxQty > 0 && quantity > maxQty) setQuantity(maxQty);
    if (quantity < minQty) setQuantity(minQty);
  }, [maxQty, minQty, quantity]);

  // ── Status pill ───────────────────────────────────────────
  const status = useMemo(() => {
    if (hasVariants) {
      if (!isVariantSelectionComplete) {
        return {
          label: "Pick options",
          fg: colors.secondaryText,
          bg: colors.hoverBg,
          icon: Package,
        };
      }
      if (selectedVariant?.is_in_stock === false || maxQty === 0) {
        return {
          label: "Out of stock",
          fg: colors.secondaryText,
          bg: colors.hoverBg,
          icon: AlertCircle,
        };
      }
      return {
        label: isPreorder ? "Pre-order open" : "Ready to ship",
        fg: isPreorder ? colors.warning : colors.success,
        bg: isPreorder ? colors.warningBg : colors.successBg,
        icon: isPreorder ? Clock : Truck,
      };
    }
    if (isPreorder) {
      return campaign
        ? {
            label: "Pre-order open",
            fg: colors.warning,
            bg: colors.warningBg,
            icon: Clock,
          }
        : {
            label: "Pre-order closed",
            fg: colors.secondaryText,
            bg: colors.hoverBg,
            icon: Clock,
          };
    }
    return product?.is_in_stock
      ? {
          label: "Ready to ship",
          fg: colors.success,
          bg: colors.successBg,
          icon: Truck,
        }
      : {
          label: "Out of stock",
          fg: colors.secondaryText,
          bg: colors.hoverBg,
          icon: AlertCircle,
        };
  }, [
    hasVariants,
    isVariantSelectionComplete,
    selectedVariant,
    isPreorder,
    campaign,
    product?.is_in_stock,
    maxQty,
    colors,
  ]);

  const StatusIcon = status.icon;

  const isAvailableInGhana = product?.sale_type === "AVAILABLE_IN_GHANA";
  const wholesale = isWholesale(product);

  // ── Shipping fee ──────────────────────────────────────────
  const shippingFee = Number(product?.shipping_fee || 0);

  // ── Actions ───────────────────────────────────────────────
  const requireLogin = () => {
    if (!isAuthenticated) {
      navigate(`/login?next=/marketplace/products/${id}`);
      return true;
    }
    return false;
  };

  const addToCart = async () => {
    if (requireLogin()) return;
    if (isUnavailable) return;
    if (hasVariants && !selectedVariant) {
      showToast("Choose all options first", "error");
      return;
    }
    if (wholesale && quantity < minQty) {
      showToast(`Minimum order is ${minQty} units`, "error");
      return;
    }
    setSaving(true);
    try {
      const body = {
        product: id,
        quantity,
        item_type: isPreorder ? "PRE_ORDER" : "DROPSHIPPING",
        ...(isPreorder && campaign && { pre_order: campaign.id }),
        ...(hasVariants && selectedVariant && { variant_key: selectedVariant.key }),
      };
      const res = await authFetch("/marketplace/cart/add/", {
        method: "POST",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(readError(data, "Could not add to cart"));
      showToast("Added to your cart", "success");
      incrementCart(quantity);
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const addToWishlist = async () => {
    if (requireLogin()) return;
    setWishlisting(true);
    try {
      const res = await authFetch("/marketplace/wishlist/add/", {
        method: "POST",
        body: JSON.stringify({ product_id: id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(readError(data, "Could not update wishlist"));
      setWishlisted(true);
      showToast(data.message || "Saved to wishlist", "success");
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setWishlisting(false);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (requireLogin()) return;
    if (!review.title.trim() || !review.comment.trim()) {
      showToast("Please fill in a title and comment", "error");
      return;
    }
    setSubmittingReview(true);
    try {
      const res = await authFetch(`/marketplace/products/${id}/reviews/`, {
        method: "POST",
        body: JSON.stringify(review),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(readError(data, "Could not submit review"));
      showToast("Review submitted for approval", "success");
      setReview({ rating: 5, title: "", comment: "" });
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setSubmittingReview(false);
    }
  };

  // ── Render: loading ───────────────────────────────────────
  if (loading) {
    return (
      <div style={styles.pageWrap}>
        <div style={styles.loadingWrap(colors)}>
          <div style={styles.spinner(colors)} />
          <span style={{ color: colors.secondaryText, fontSize: 13 }}>
            Loading product…
          </span>
        </div>
      </div>
    );
  }

  // ── Render: error ─────────────────────────────────────────
  if (error || !product) {
    return (
      <div style={styles.pageWrap}>
        <div style={styles.errorWrap(colors)}>
          <div style={styles.errorIcon(colors)}>
            <AlertCircle size={26} />
          </div>
          <h2 style={styles.errorTitle(colors)}>Product not found</h2>
          <p style={styles.errorText(colors)}>
            {error || "This product may have been removed."}
          </p>
          <Link to="/marketplace" style={styles.primaryCta(colors)}>
            Back to marketplace
          </Link>
        </div>
      </div>
    );
  }

  const imageSrc = product.main_image_url || product.main_image;

  return (
    <div style={styles.pageWrap}>
      <button
        type="button"
        onClick={() => navigate(-1)}
        style={styles.backBtn(colors)}
      >
        <ChevronLeft size={16} />
        Back
      </button>

      <div style={styles.layout} className="product-detail-layout">
        {/* ── Image ────────────────────────────────────── */}
        <div style={styles.imageFrame(colors)}>
          {imageSrc ? (
            <img src={imageSrc} alt={product.name} style={styles.image} />
          ) : (
            <div style={styles.imagePlaceholder(colors)}>
              <Package size={36} />
            </div>
          )}

          {/* Status pill (top-left) */}
          <span
            style={{
              position: "absolute",
              top: 14,
              left: 14,
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              padding: "5px 11px",
              borderRadius: 999,
              background: status.bg,
              color: status.fg,
              fontSize: 11,
              fontWeight: 800,
              letterSpacing: 0.3,
              textTransform: "uppercase",
              backdropFilter: "blur(8px)",
            }}
          >
            <StatusIcon size={11} />
            {status.label}
          </span>

          {/* Ghana badge (top-right) */}
          {isAvailableInGhana && (
            <span
              style={{
                position: "absolute",
                top: 14,
                right: 14,
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 11px",
                borderRadius: 999,
                background: colors.btnBg,
                color: colors.btnText,
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: 0.3,
                textTransform: "uppercase",
                boxShadow: `0 2px 8px ${colors.shadow}`,
              }}
            >
              <BadgeCheck size={11} />
              In Ghana
            </span>
          )}

          {/* Wholesale badge (stacks under Ghana if both) */}
          {wholesale && (
            <span
              style={{
                position: "absolute",
                top: isAvailableInGhana ? 52 : 14,
                right: 14,
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 11px",
                borderRadius: 999,
                background: colors.heading,
                color: colors.mainBg,
                fontSize: 10.5,
                fontWeight: 800,
                letterSpacing: 0.3,
                textTransform: "uppercase",
                boxShadow: `0 2px 8px ${colors.shadow}`,
              }}
            >
              <Boxes size={11} />
              Wholesale · MOQ {Number(product.moq)}
            </span>
          )}
        </div>

        {/* ── Info ─────────────────────────────────────── */}
        <div>
          <h1 style={styles.title(colors)}>{product.name}</h1>

          <Rating
            value={Number(product.average_rating || 0)}
            count={product.review_count || 0}
            colors={colors}
          />

          <p style={styles.shortDesc(colors)}>
            {product.short_description || product.description}
          </p>

          <div style={styles.priceRow(colors)}>
            <span style={styles.price(colors)}>{money(displayPrice)}</span>
            {isPreorder && campaign && (
              <span style={styles.preorderPill(colors)}>
                <Clock size={12} /> Pre-order price
              </span>
            )}
            {wholesale && (
              <span style={styles.wholesalePill(colors)}>
                <Boxes size={12} /> Wholesale
              </span>
            )}
          </div>

          {/* Pre-order window card */}
          {isPreorder && (
            <div
              style={{
                ...styles.windowCard(colors),
                background: campaign ? colors.warningBg : colors.hoverBg,
                borderColor: campaign ? `${colors.warning}55` : colors.border,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 4,
                  color: campaign ? colors.warning : colors.secondaryText,
                  fontWeight: 800,
                  fontSize: 12,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                }}
              >
                <Clock size={13} />
                {campaign ? "Order window open" : "No open window"}
              </div>
              <p
                style={{
                  margin: 0,
                  fontSize: 13.5,
                  lineHeight: 1.55,
                  color: colors.text,
                }}
              >
                {campaign ? (
                  <>
                    Order by{" "}
                    <strong style={{ color: colors.heading }}>
                      {new Date(campaign.end_date).toLocaleString()}
                    </strong>
                    . All orders in this window ship together.
                  </>
                ) : (
                  "This product is pre-order only. It's currently between campaigns — check back soon."
                )}
              </p>
              {campaign && (
                <Link
                  to={`/preorder/campaigns/${campaign.id}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    marginTop: 10,
                    fontSize: 12.5,
                    fontWeight: 700,
                    color: colors.linkColor,
                    textDecoration: "none",
                  }}
                >
                  See all products in this window
                  <ChevronRight size={13} />
                </Link>
              )}
            </div>
          )}

          {/* ── Variant selector ─────────────────────────── */}
          {hasVariants && attributeNames.length > 0 && (
            <div style={styles.variantSection}>
              {attributeNames.map((name) => (
                <VariantRow
                  key={name}
                  name={name}
                  values={valuesByName[name] || []}
                  selection={selection}
                  availability={availabilityByAttr[name] || {}}
                  onChange={(value) =>
                    setSelection((prev) => ({ ...prev, [name]: value }))
                  }
                  colors={colors}
                />
              ))}

              {!isVariantSelectionComplete && (
                <div style={styles.variantHint(colors)}>
                  <AlertCircle size={13} />
                  Choose a value for every option to see the final price and
                  availability.
                </div>
              )}

              {isVariantSelectionComplete && !selectedVariant && (
                <div style={styles.variantError(colors)}>
                  <Ban size={13} />
                  This combination is not available. Try another.
                </div>
              )}
            </div>
          )}

          {/* ── Quantity ─────────────────────────────────── */}
          {!isUnavailable && (
            <div style={{ marginTop: 20 }}>
              <div style={styles.qtyLabel(colors)}>
                Quantity
                {wholesale && (
                  <span style={styles.moqChip(colors)}>
                    Min {Number(product.moq)} units
                  </span>
                )}
                {maxQty > 0 && maxQty < 999 && (
                  <span style={{ color: colors.secondaryText, fontWeight: 500 }}>
                    {" "}
                    · {maxQty} available
                  </span>
                )}
              </div>

              {/* Wholesale helper line */}
              {wholesale && (
                <div style={styles.moqHint(colors)}>
                  <Boxes size={13} />
                  Wholesale orders ship as one batch. Minimum is{" "}
                  <strong style={{ color: colors.heading }}>
                    {Number(product.moq)} units
                  </strong>{" "}
                  per order.
                </div>
              )}

              <div style={styles.qtyGroup(colors)}>
                <button
                  type="button"
                  aria-label="Decrease quantity"
                  disabled={quantity <= minQty}
                  onClick={() => setQuantity((q) => Math.max(minQty, q - 1))}
                  style={styles.qtyBtn(colors, quantity <= minQty)}
                >
                  <Minus size={15} />
                </button>
                <span
                  style={{
                    minWidth: 40,
                    textAlign: "center",
                    fontWeight: 800,
                    fontSize: 15,
                    color: colors.heading,
                  }}
                >
                  {quantity}
                </span>
                <button
                  type="button"
                  aria-label="Increase quantity"
                  disabled={quantity >= maxQty}
                  onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                  style={styles.qtyBtn(colors, quantity >= maxQty)}
                >
                  <Plus size={15} />
                </button>
              </div>
            </div>
          )}

          {/* ── Actions ──────────────────────────────────── */}
          <div style={styles.actionsRow}>
            <button
              type="button"
              disabled={isUnavailable || saving}
              onClick={addToCart}
              style={styles.primaryAction(colors, isUnavailable || saving)}
            >
              <ShoppingBag size={17} />
              {saving
                ? "Adding…"
                : isUnavailable
                ? hasVariants && !isVariantSelectionComplete
                  ? "Pick options"
                  : "Unavailable"
                : isPreorder
                ? "Join pre-order"
                : wholesale
                ? `Add ${quantity} to cart`
                : "Add to cart"}
            </button>
            <button
              type="button"
              aria-label="Save to wishlist"
              disabled={wishlisting || wishlisted}
              onClick={addToWishlist}
              style={styles.wishlistBtn(colors, wishlisted)}
            >
              <Heart size={19} fill={wishlisted ? colors.error : "none"} />
            </button>
          </div>

          {/* ── Shipping + benefits ──────────────────────── */}
          <div style={styles.shippingLine(colors)}>
            <Truck
              size={14}
              color={shippingFee > 0 ? colors.text : colors.success}
            />
            <span
              style={{
                color: shippingFee > 0 ? colors.text : colors.success,
                fontWeight: 700,
              }}
            >
              {shippingFee > 0
                ? `Est. shipping ${money(shippingFee)}/unit — paid on arrival`
                : "Free shipping"}
            </span>
          </div>

          <div style={styles.benefitsGrid}>
            <Benefit
              icon={ShieldCheck}
              title="Protected payment"
              copy="Wallet or Paystack secured"
              colors={colors}
            />
            <Benefit
              icon={Undo2}
              title="Order support"
              copy="Track every order"
              colors={colors}
            />
            <Benefit
              icon={Package}
              title="Careful packing"
              copy="Inspect before shipping"
              colors={colors}
            />
          </div>
        </div>
      </div>

      {/* ── Description ──────────────────────────────── */}
      <section style={styles.section(colors)}>
        <SectionHead
          title="About this product"
          subtitle="Product information supplied by Azoemart."
          colors={colors}
        />
        <p style={styles.body(colors)}>
          {product.description || "No further description provided."}
        </p>
      </section>

      {/* ── Reviews ──────────────────────────────────── */}
      <section style={styles.section(colors)}>
        <SectionHead
          title="Customer reviews"
          subtitle={`${reviews.length} approved review${
            reviews.length === 1 ? "" : "s"
          }`}
          colors={colors}
        />

        {reviews.length === 0 ? (
          <div style={styles.noReviews(colors)}>
            <MessageSquare size={22} />
            <p style={{ margin: "8px 0 0" }}>
              No approved reviews yet. Be the first to share your experience.
            </p>
          </div>
        ) : (
          <div style={{ display: "grid", gap: 14 }}>
            {reviews.map((r) => (
              <article key={r.id} style={styles.reviewCard(colors)}>
                <div style={styles.reviewHeader}>
                  <div>
                    <div style={styles.reviewTitle(colors)}>{r.title}</div>
                    <div style={styles.reviewMeta(colors)}>
                      {r.username} ·{" "}
                      {new Date(r.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <Stars value={r.rating} colors={colors} size={13} />
                </div>
                <p style={styles.reviewBody(colors)}>{r.comment}</p>
              </article>
            ))}
          </div>
        )}

        <form onSubmit={submitReview} style={styles.reviewForm(colors)}>
          <h3 style={styles.reviewFormTitle(colors)}>Write a review</h3>
          <p style={styles.reviewFormSub(colors)}>
            Reviews are moderated before appearing publicly.
          </p>

          <div style={styles.formGrid} className="product-form-grid">
            <label style={styles.fieldWrap}>
              <span style={styles.fieldLabel(colors)}>Rating</span>
              <select
                value={review.rating}
                onChange={(e) =>
                  setReview({ ...review, rating: Number(e.target.value) })
                }
                style={styles.select(colors)}
              >
                {[5, 4, 3, 2, 1].map((n) => (
                  <option key={n} value={n}>
                    {n} star{n === 1 ? "" : "s"}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.fieldWrap}>
              <span style={styles.fieldLabel(colors)}>Title</span>
              <input
                required
                value={review.title}
                onChange={(e) =>
                  setReview({ ...review, title: e.target.value })
                }
                placeholder="Short headline"
                style={styles.input(colors)}
              />
            </label>
          </div>

          <label style={{ ...styles.fieldWrap, marginTop: 12 }}>
            <span style={styles.fieldLabel(colors)}>Comment</span>
            <textarea
              required
              rows={4}
              value={review.comment}
              onChange={(e) =>
                setReview({ ...review, comment: e.target.value })
              }
              placeholder="Tell other customers about your experience"
              style={styles.textarea(colors)}
            />
          </label>

          <div
            style={{
              marginTop: 14,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <button
              type="submit"
              disabled={submittingReview}
              style={styles.submitReview(colors, submittingReview)}
            >
              {submittingReview ? "Submitting…" : "Submit review"}
            </button>
          </div>
        </form>
      </section>

      <style>{`
        @media (min-width: 900px) {
          .product-detail-layout {
            grid-template-columns: minmax(0, 1.05fr) minmax(0, 1fr) !important;
          }
        }
        @media (min-width: 640px) {
          .product-form-grid {
            grid-template-columns: 160px minmax(0, 1fr) !important;
          }
        }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Variant row
   ══════════════════════════════════════════════════════════ */

const VariantRow = ({ name, values, selection, availability, onChange, colors }) => {
  const currentValue = selection[name];
  return (
    <div style={styles.variantRow}>
      <div style={styles.variantLabel(colors)}>
        {name}
        {currentValue && (
          <span style={styles.variantPicked(colors)}>{currentValue}</span>
        )}
      </div>
      <div style={styles.variantPills}>
        {values.map((v) => {
          const enabled = availability[v] !== false;
          const active = currentValue === v;
          return (
            <button
              key={v}
              type="button"
              disabled={!enabled}
              onClick={() => enabled && onChange(v)}
              title={
                enabled
                  ? undefined
                  : currentValue
                  ? `Not available with ${currentValue}`
                  : "Not available"
              }
              style={styles.variantPill(colors, active, !enabled)}
            >
              {v}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   Small building blocks
   ══════════════════════════════════════════════════════════ */

const Stars = ({ value = 0, colors, size = 14 }) => {
  const rounded = Math.round(value * 2) / 2;
  return (
    <span style={{ display: "inline-flex", gap: 1 }}>
      {[1, 2, 3, 4, 5].map((i) => {
        const fill =
          i <= Math.floor(rounded)
            ? "full"
            : i - 0.5 === rounded
            ? "half"
            : "empty";
        return (
          <Star
            key={i}
            size={size}
            strokeWidth={1.5}
            color={colors.warning}
            fill={fill === "full" ? colors.warning : "none"}
          />
        );
      })}
    </span>
  );
};

const Rating = ({ value, count, colors }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 10 }}>
    <Stars value={value} colors={colors} size={15} />
    <span style={{ fontSize: 13, color: colors.secondaryText }}>
      {value > 0 ? value.toFixed(1) : "New"} · {count} review
      {count === 1 ? "" : "s"}
    </span>
  </div>
);

const Benefit = ({ icon: Icon, title, copy, colors }) => (
  <div
    style={{
      padding: 12,
      borderRadius: 12,
      border: `1px solid ${colors.border}`,
      background: colors.raisedBg,
    }}
  >
    <Icon size={16} color={colors.btnBg} />
    <div
      style={{
        fontSize: 12.5,
        fontWeight: 700,
        color: colors.heading,
        marginTop: 8,
        marginBottom: 2,
      }}
    >
      {title}
    </div>
    <div
      style={{
        fontSize: 11.5,
        color: colors.secondaryText,
        lineHeight: 1.4,
      }}
    >
      {copy}
    </div>
  </div>
);

const SectionHead = ({ title, subtitle, colors }) => (
  <div style={{ marginBottom: 14 }}>
    <h2
      style={{
        margin: 0,
        fontSize: 17,
        fontWeight: 800,
        color: colors.heading,
        letterSpacing: "-0.01em",
      }}
    >
      {title}
    </h2>
    {subtitle && (
      <p
        style={{
          margin: "4px 0 0",
          fontSize: 13,
          color: colors.secondaryText,
        }}
      >
        {subtitle}
      </p>
    )}
  </div>
);

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  pageWrap: {
    maxWidth: 1180,
    margin: "0 auto",
    padding: "16px 16px 64px",
  },

  backBtn: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    background: "transparent",
    border: "none",
    color: c.secondaryText,
    cursor: "pointer",
    padding: "6px 0",
    marginBottom: 14,
    fontSize: 13,
    fontWeight: 600,
  }),

  layout: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 28,
    marginBottom: 40,
  },

  imageFrame: (c) => ({
    position: "relative",
    aspectRatio: "1 / 1",
    borderRadius: 18,
    overflow: "hidden",
    border: `1px solid ${c.border}`,
    background: c.subtleBg || c.hoverBg,
  }),

  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },

  imagePlaceholder: (c) => ({
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: c.secondaryText,
  }),

  title: (c) => ({
    margin: 0,
    fontSize: "clamp(22px, 3vw, 30px)",
    fontWeight: 800,
    letterSpacing: "-0.02em",
    lineHeight: 1.15,
    color: c.heading,
  }),

  shortDesc: (c) => ({
    margin: "16px 0 0",
    fontSize: 14.5,
    lineHeight: 1.6,
    color: c.secondaryText,
  }),

  priceRow: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
    marginTop: 18,
  }),

  price: (c) => ({
    fontSize: 28,
    fontWeight: 800,
    letterSpacing: "-0.02em",
    color: c.heading,
  }),

  preorderPill: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 10px",
    borderRadius: 999,
    background: c.warningBg,
    color: c.warning,
    fontSize: 11,
    fontWeight: 700,
  }),

  wholesalePill: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "4px 10px",
    borderRadius: 999,
    background: `${c.btnBg}18`,
    color: c.btnBg,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.2,
  }),

  windowCard: (c) => ({
    marginTop: 18,
    padding: 14,
    borderRadius: 12,
    border: `1px solid ${c.border}`,
  }),

  // ── Variant selector ─────────────────────────────────
  variantSection: {
    marginTop: 22,
    display: "grid",
    gap: 14,
  },
  variantRow: {
    display: "grid",
    gap: 8,
  },
  variantLabel: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    color: c.secondaryText,
  }),
  variantPicked: (c) => ({
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.2,
    padding: "2px 8px",
    borderRadius: 999,
    background: c.hoverBg,
    color: c.text,
    textTransform: "none",
  }),
  variantPills: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
  },
  variantPill: (c, active, disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 44,
    padding: "8px 14px",
    borderRadius: 10,
    border: `1px solid ${
      disabled ? c.border : active ? c.btnBg : c.border
    }`,
    background: disabled ? c.hoverBg : active ? c.btnBg : c.raisedBg,
    color: disabled ? c.secondaryText : active ? c.btnText : c.text,
    fontSize: 13.5,
    fontWeight: 700,
    fontFamily: "inherit",
    cursor: disabled ? "not-allowed" : "pointer",
    textDecoration: disabled ? "line-through" : "none",
    opacity: disabled ? 0.6 : 1,
    transition: "background 0.15s ease, border-color 0.15s ease",
  }),

  variantHint: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: c.secondaryText,
    paddingTop: 2,
  }),
  variantError: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12.5,
    fontWeight: 600,
    color: c.error,
    paddingTop: 2,
  }),

  // ── Quantity ─────────────────────────────────────────
  qtyLabel: (c) => ({
    fontSize: 13,
    fontWeight: 700,
    color: c.text,
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexWrap: "wrap",
  }),
  moqChip: (c) => ({
    fontSize: 10.5,
    fontWeight: 800,
    letterSpacing: 0.3,
    textTransform: "uppercase",
    padding: "2px 8px",
    borderRadius: 999,
    background: `${c.btnBg}18`,
    color: c.btnBg,
  }),
  moqHint: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    color: c.secondaryText,
    marginBottom: 10,
    lineHeight: 1.5,
  }),
  qtyGroup: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    border: `1px solid ${c.border}`,
    borderRadius: 12,
    background: c.raisedBg,
    overflow: "hidden",
  }),
  qtyBtn: (c, disabled) => ({
    width: 40,
    height: 40,
    border: "none",
    background: "transparent",
    color: disabled ? c.secondaryText : c.text,
    cursor: disabled ? "not-allowed" : "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    opacity: disabled ? 0.4 : 1,
  }),

  // ── Actions ──────────────────────────────────────────
  actionsRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    gap: 10,
    marginTop: 22,
  },
  primaryAction: (c, disabled) => ({
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    padding: "13px 22px",
    borderRadius: 12,
    border: "none",
    background: disabled ? c.subtleBg || c.hoverBg : c.btnBg,
    color: disabled ? c.secondaryText : c.btnText,
    fontSize: 14.5,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    boxShadow: disabled ? "none" : `0 6px 20px ${c.btnBg}33`,
  }),
  wishlistBtn: (c, active) => ({
    width: 52,
    height: 52,
    borderRadius: 12,
    border: `1px solid ${active ? c.error : c.border}`,
    background: active ? c.errorBg : c.raisedBg,
    color: active ? c.error : c.text,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "border-color 0.15s ease, background 0.15s ease",
  }),

  // ── Shipping line ────────────────────────────────────
  shippingLine: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 18,
    padding: "10px 14px",
    borderRadius: 10,
    background: c.subtleBg || c.hoverBg,
    border: `1px solid ${c.border}`,
    fontSize: 13,
  }),

  benefitsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
    gap: 10,
    marginTop: 14,
  },

  // ── Sections ─────────────────────────────────────────
  section: (c) => ({
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    borderRadius: 16,
    padding: 20,
    marginBottom: 20,
  }),
  body: (c) => ({
    margin: 0,
    fontSize: 14,
    lineHeight: 1.7,
    color: c.text,
  }),

  // ── Reviews ──────────────────────────────────────────
  noReviews: (c) => ({
    padding: 28,
    textAlign: "center",
    color: c.secondaryText,
    border: `1px dashed ${c.border}`,
    borderRadius: 12,
    fontSize: 13,
  }),
  reviewCard: (c) => ({
    padding: 14,
    borderRadius: 12,
    border: `1px solid ${c.border}`,
    background: c.cardBg,
  }),
  reviewHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 10,
    marginBottom: 8,
  },
  reviewTitle: (c) => ({
    fontSize: 14,
    fontWeight: 700,
    color: c.heading,
    marginBottom: 2,
  }),
  reviewMeta: (c) => ({
    fontSize: 11.5,
    color: c.secondaryText,
  }),
  reviewBody: (c) => ({
    margin: 0,
    fontSize: 13.5,
    lineHeight: 1.6,
    color: c.text,
  }),
  reviewForm: (c) => ({
    marginTop: 22,
    paddingTop: 20,
    borderTop: `1px solid ${c.separator || c.border}`,
  }),
  reviewFormTitle: (c) => ({
    margin: 0,
    fontSize: 15,
    fontWeight: 800,
    color: c.heading,
  }),
  reviewFormSub: (c) => ({
    margin: "4px 0 16px",
    fontSize: 12.5,
    color: c.secondaryText,
  }),
  formGrid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr)",
    gap: 12,
  },
  fieldWrap: {
    display: "block",
    minWidth: 0,
  },
  fieldLabel: (c) => ({
    display: "block",
    fontSize: 12,
    fontWeight: 600,
    color: c.text,
    marginBottom: 6,
  }),
  input: (c) => ({
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${c.border}`,
    background: c.raisedBg,
    color: c.text,
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
  }),
  select: (c) => ({
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${c.border}`,
    background: c.raisedBg,
    color: c.text,
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
  }),
  textarea: (c) => ({
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 12px",
    borderRadius: 10,
    border: `1px solid ${c.border}`,
    background: c.raisedBg,
    color: c.text,
    fontSize: 14,
    outline: "none",
    fontFamily: "inherit",
    resize: "vertical",
    minHeight: 96,
  }),
  submitReview: (c, disabled) => ({
    padding: "10px 20px",
    borderRadius: 10,
    border: "none",
    background: disabled ? c.subtleBg || c.hoverBg : c.btnBg,
    color: disabled ? c.secondaryText : c.btnText,
    fontSize: 13.5,
    fontWeight: 700,
    cursor: disabled ? "not-allowed" : "pointer",
    transition: "transform 0.15s ease",
  }),

  // ── Loading / error ──────────────────────────────────
  loadingWrap: (c) => ({
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 360,
    gap: 14,
  }),
  spinner: (c) => ({
    width: 34,
    height: 34,
    border: `3px solid ${c.border}`,
    borderTopColor: c.btnBg,
    borderRadius: "50%",
    animation: "spin 0.9s linear infinite",
  }),
  errorWrap: (c) => ({
    maxWidth: 380,
    margin: "60px auto",
    textAlign: "center",
    padding: 24,
  }),
  errorIcon: (c) => ({
    width: 64,
    height: 64,
    borderRadius: 20,
    background: c.hoverBg,
    color: c.secondaryText,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  }),
  errorTitle: (c) => ({
    margin: "0 0 6px",
    fontSize: 18,
    fontWeight: 800,
    color: c.heading,
  }),
  errorText: (c) => ({
    margin: "0 0 22px",
    fontSize: 13.5,
    color: c.secondaryText,
    lineHeight: 1.55,
  }),
  primaryCta: (c) => ({
    display: "inline-block",
    padding: "11px 20px",
    borderRadius: 10,
    background: c.btnBg,
    color: c.btnText,
    fontWeight: 700,
    fontSize: 13.5,
    textDecoration: "none",
  }),
};